<template>
  <m-card
    class="network-controls"
    padding-xs
  >
    <div class="_content">
      <div class="_action">
        <m-btn
          class="_btn"
          :to="goalsExplorerLink"
          icon="arrow-left"
          fab
          hide-border
          small
        />
      </div>
      <saved-view-selector
        :allowed-view-types="allowedViewTypes"
        :view-application="viewApplication"
        show-create
      />
      <div class="_select">
        <m-select
          :items="items"
          :value="currentView.params.grouping"
          :disabled="!canEdit(currentView)"
          small
          btn
          hide-border
          @change="updateGrouping"
        />
        <goal-cycle-app-settings-selector
          :selected="selectedCycles"
          :change-selected-goal-cycles="changeSelectedGoalCycles"
          editable
          with-unassigned
        />
        <props-filter
          :value="filteredSavedViewProps"
          :saved-view="currentView"
          :disabled="!canEdit(currentView)"
          @input="setParams($event, 'props')"
        >
          <template
            #header
          >
            <m-card-item
              no-hover
              :clickable="false"
              :style="{ marginBottom: '.4rem' }"
            >
              <saved-view-info
                :view="currentView"
                :label="$t('savedViewInfo.propertyLabel')"
                class="_info"
              />
            </m-card-item>
          </template>
        </props-filter>
        <list-filter
          :props="filterProps"
          :value="currentView.params.filter"
          has-dynamic-dates
          :saved-view="currentView"
          show-saved-view-info
          :disabled="!canEdit(currentView)"
          @input="updateFilter"
        />
        <list-string-filter
          small
          @input="$emit('string-filter-changed', $event)"
        />
      </div>
    </div>
  </m-card>
</template>

<script>
import GoalCycleAppSettingsSelector from '@/components/goal/cycle/GoalCycleAppSettingsSelector.vue';
import ListFilter from '@/components/list/ListFilter.vue';
import ListStringFilter from '@/components/list/ListStringFilter.vue';
import PropsFilter from '@/components/PropsFilter.vue';
import SavedViewInfo from '@/components/list/SavedViewInfo.vue';
import SavedViewSelector from '@/components/list/SavedViewSelector.vue';
import useGoalProperty from '@/composables/property/goal-property';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useViewRights from '@/composables/view-rights';
import { VIEWS_SERVICE } from '@/lib/constants';
import { inject } from 'vue';
import { propertyType, routeName } from 'shared/constants.json';
import { textByLang } from 'shared/lib/language';

export default {
  name: 'AtlasControls',
  props: {
    filterProps: {
      type: Array,
      default: () => [],
    },
    viewApplication: {
      type: String,
      default: '',
    },
    allowedViewTypes: {
      type: Array,
      default: () => [],
    },
    selectedCycles: {
      type: Array,
      default: () => [],
    },
    changeSelectedGoalCycles: {
      type: Function,
      default: () => {
      },
    },
  },
  setup(props) {
    const { goalSettings } = useGoalSettings();
    const { statusProperty } = useGoalProperty();
    const viewsService = inject(VIEWS_SERVICE);
    const { userLang } = useLoggedInUser();
    const { canEdit } = useViewRights(false, props.viewApplication);
    return {
      goalSettings,
      viewsService,
      currentView: viewsService.currentView,
      statusLabel: textByLang(statusProperty.value.label, userLang.value),
      canEdit,
    };
  },
  components: {
    ListStringFilter,
    PropsFilter,
    SavedViewSelector,
    SavedViewInfo,
    ListFilter,
    GoalCycleAppSettingsSelector,
  },
  emits: ['string-filter-changed'],
  computed: {
    filteredSavedViewProps() {
      return this.currentView.params.props.filter((p) => !p.isDirect && [propertyType.options, propertyType.space].includes(p.property.type));
    },
    goalsExplorerLink() {
      return { name: routeName.goalsExplorer };
    },
    items() {
      return [
        {
          text: this.$t('networkControls.colorByStatus', { statusLabel: this.statusLabel }),
          value: 'status',
        },
        {
          text: this.$t('networkControls.progress'),
          value: 'progress',
        },
      ];
    },
  },
  methods: {
    updateGrouping(value) {
      this.setParams(value, 'grouping');
    },
    updateFilter(value) {
      this.setParams(value, 'filter');
    },
    setParams(value, key) {
      this.viewsService.setParams(
        this.currentView,
        {
          ...this.currentView.params,
          [key]: value,
        },
      );
    },
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  .network-controls {
    position: absolute;
    top: 2rem;
    left: 2rem;
    z-index: 1;

    ._content {
      display: flex;
      align-items: center;
      justify-content: center;

      ._action {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-right: 1rem;
        margin-right: 1rem;
        border-right: 1px solid $input-border-color;
      }

      ._select {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: .6rem;
      }
    }
  }
</style>
